import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getAdjacentModules,
  getModuleByPath,
  getModuleStartSlug,
  NUMEROLOGY_COURSE,
} from '../../data/numerologyCourse';
import '../../components/numerology/numerologyCourse.css';

export default function NumerologyModulePage() {
  const { slug } = useParams();
  const mod = getModuleByPath(slug);
  const { prev, next } = getAdjacentModules(slug);
  const startSlug = getModuleStartSlug(mod);

  useEffect(() => {
    const prevTitle = document.title;
    if (mod) {
      document.title = `Module ${mod.number} — ${mod.title} | Numerology Course | Astral Moon Magic`;
    }
    return () => {
      document.title = prevTitle;
    };
  }, [mod]);

  if (!mod) {
    return (
      <div className="amm-num-course">
        <p>Module not found.</p>
        <Link className="amm-text-link" to="/numerology/study">
          Back to course
        </Link>
      </div>
    );
  }

  const lessonCount = mod.lessons.length;
  const optionalCount = mod.optionalLessons?.length ?? 0;

  return (
    <div className="amm-num-course">
      <header className="amm-num-course__header">
        <p className="amm-num-course__eyebrow">Module {mod.number} of {NUMEROLOGY_COURSE.length}</p>
        <h1 className="amm-num-course__title">{mod.title}</h1>
        <p className="amm-num-course__outcome">
          <span className="amm-num-course__outcome-label">After this module:</span> {mod.outcome}
        </p>
        <p className="amm-num-course__lead">{mod.description}</p>
      </header>

      <section className="amm-num-course__lessons" aria-labelledby={`lessons-${mod.id}`}>
        <h2 id={`lessons-${mod.id}`} className="amm-num-course__h2">
          Lessons ({lessonCount}
          {optionalCount ? ` · ${optionalCount} optional` : ''})
        </h2>
        <ol className="amm-num-course__lesson-list">
          {mod.lessons.map((lesson, i) => (
            <li key={lesson.slug || lesson.to} className="amm-num-course__lesson-item">
              <span className="amm-num-course__lesson-idx" aria-hidden>
                {i + 1}.
              </span>
              {lesson.type === 'route' ? (
                <Link className="amm-num-course__lesson-link" to={lesson.to}>
                  {lesson.label}
                </Link>
              ) : (
                <Link className="amm-num-course__lesson-link" to={`/numerology/study/${lesson.slug}`}>
                  {lesson.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
        {optionalCount ? (
          <div className="amm-num-course__optional" aria-labelledby={`optional-${mod.id}`}>
            <h3 id={`optional-${mod.id}`} className="amm-num-course__h3">
              Optional deep dives
            </h3>
            <p className="amm-num-course__optional-lead">
              Not required for the linear path — open when you want extra practice on a single name angle.
            </p>
            <ul className="amm-num-course__lesson-list amm-num-course__lesson-list--optional">
              {mod.optionalLessons.map((lesson) => (
                <li key={lesson.slug} className="amm-num-course__lesson-item">
                  <Link className="amm-num-course__lesson-link" to={`/numerology/study/${lesson.slug}`}>
                    {lesson.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <div className="amm-num-course__actions">
        {startSlug ? (
          <Link className="amm-button" to={`/numerology/study/${startSlug}`}>
            Start module
          </Link>
        ) : null}
      </div>

      <nav className="amm-num-course__nav" aria-label="Module navigation">
        {prev ? (
          <Link className="amm-num-course__nav-link amm-num-course__nav-link--prev" to={`/numerology/study/${prev.path}`}>
            <span className="amm-num-course__nav-dir">Previous</span>
            <span className="amm-num-course__nav-title">Module {prev.number} — {prev.title}</span>
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link className="amm-num-course__nav-link amm-num-course__nav-link--next" to={`/numerology/study/${next.path}`}>
            <span className="amm-num-course__nav-dir">Next</span>
            <span className="amm-num-course__nav-title">Module {next.number} — {next.title}</span>
          </Link>
        ) : null}
      </nav>

      <p className="amm-num-course__back">
        <Link className="amm-text-link" to="/numerology/study">
          Back to course
        </Link>
      </p>
    </div>
  );
}
